/**
 * Vitly Design System Constants
 * Centralized design tokens for consistent UI across the application.
 * Colors map to CSS vars from theme-colors.css (teal primary / premium CTA).
 */

// Color System
export const colors = {
  // Primary colors — teal scale via CSS vars (theme-colors.css)
  primary: {
    50: 'bg-teal-1',
    100: 'bg-teal-2',
    200: 'bg-teal-3',
    300: 'bg-teal-4',
    400: 'bg-teal-5',
    500: 'bg-teal-6',
    600: 'bg-primary',
    700: 'bg-primary-hover',
    800: 'bg-primary-active',
    900: 'bg-teal-12',
  },

  // Text colors with clear hierarchy (semantic CSS vars)
  text: {
    primary: 'text-text-primary',
    secondary: 'text-text-secondary',
    muted: 'text-text-tertiary',
    disabled: 'text-text-subtle',
    inverse: 'text-white',
    error: 'text-red-600 dark:text-red-400',
    success: 'text-green-600 dark:text-green-400',
    warning: 'text-yellow-600 dark:text-yellow-400',
    link: 'text-primary hover:text-primary-hover',
  },

  // Background colors with consistent usage
  background: {
    main: 'bg-background',
    section: 'bg-background-secondary',
    elevated: 'bg-background-elevated',
    dark: 'bg-gray-12 dark:bg-gray-1',
    darker: 'bg-gray-12',
    overlay: 'bg-background-overlay',
    hover: {
      light: 'hover:bg-background-secondary',
      dark: 'hover:bg-background-tertiary',
    },
  },

  // Border colors
  border: {
    default: 'border-border',
    light: 'border-border-subtle',
    focus: 'focus:border-primary',
  },
};

// Typography Scale
export const typography = {
  // Headings with consistent sizing
  h1: 'text-4xl lg:text-5xl font-bold',
  h2: 'text-3xl lg:text-4xl font-bold',
  h3: 'text-2xl lg:text-3xl font-semibold',
  h4: 'text-xl lg:text-2xl font-semibold',
  h5: 'text-lg lg:text-xl font-medium',
  h6: 'text-base lg:text-lg font-medium',

  // Body text sizes
  body: {
    large: 'text-lg',
    base: 'text-base',
    small: 'text-sm',
    xsmall: 'text-xs',
  },

  // Special text styles
  lead: 'text-xl text-text-secondary',
  caption: 'text-sm text-text-tertiary',
  label: 'text-sm font-medium text-text-secondary',
};

// Button Styles
export const buttons = {
  // Base button styles
  base: 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',

  // Size variants
  size: {
    xs: 'px-2.5 py-1.5 text-xs',
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
    xl: 'px-8 py-4 text-xl',
  },

  // Color variants — primary uses theme primary (teal/blue) or premium CTA tokens
  variant: {
    primary:
      'bg-primary text-white hover:bg-primary-hover active:bg-primary-active focus:ring-primary',
    secondary:
      'bg-secondary text-white hover:bg-secondary-hover active:bg-secondary-active focus:ring-secondary',
    success: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
    warning: 'bg-yellow-500 text-white hover:bg-yellow-600 focus:ring-yellow-500',
    ghost:
      'text-text-secondary hover:bg-background-secondary hover:text-text-primary focus:ring-secondary',
    outline:
      'border border-border bg-background text-text-primary hover:bg-background-secondary focus:ring-primary',
    link: 'text-primary hover:text-primary-hover underline-offset-4 hover:underline',
    'ios-primary': 'btn-ios-primary',
    'ios-secondary': 'btn-ios-secondary',
    'ios-ghost': 'btn-ios-ghost',
  },

  // Icon button styles
  icon: {
    base: 'inline-flex items-center justify-center rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2',
    size: {
      sm: 'p-1',
      md: 'p-2',
      lg: 'p-3',
    },
  },
};

// Spacing Scale
export const spacing = {
  section: {
    xs: 'py-8',
    sm: 'py-12',
    md: 'py-16',
    lg: 'py-20',
    xl: 'py-24',
  },
  container: {
    padding: 'px-4 sm:px-6 lg:px-8',
    maxWidth: 'max-w-7xl mx-auto',
  },
  stack: {
    xs: 'space-y-2',
    sm: 'space-y-4',
    md: 'space-y-6',
    lg: 'space-y-8',
    xl: 'space-y-12',
  },
};

// Animation & Transitions
export const animations = {
  transition: {
    fast: 'transition-all duration-150',
    base: 'transition-all duration-200',
    slow: 'transition-all duration-300',
  },
  hover: {
    scale: 'hover:scale-105 active:scale-95',
    lift: 'hover:-translate-y-1',
    glow: 'hover:shadow-lg',
  },
};

// Form Styles
export const forms = {
  input: {
    base: 'w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors',
    default: 'border-border bg-background text-text-primary',
    error: 'border-red-500 focus:ring-red-500 focus:border-red-500',
    success: 'border-green-500 focus:ring-green-500 focus:border-green-500',
  },
  label: 'block text-sm font-medium text-text-secondary mb-2',
  helper: 'mt-1 text-sm text-text-tertiary',
  error: 'mt-1 text-sm text-red-600 dark:text-red-400',
};

// Card Styles
export const cards = {
  base: 'bg-background-elevated rounded-lg shadow-md border border-border',
  hover: 'hover:shadow-lg transition-shadow duration-200',
  padding: {
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  },
};

// Badge Styles
export const badges = {
  base: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
  variant: {
    default: 'bg-background-secondary text-text-primary',
    primary: 'bg-primary-subtle text-primary-active',
    success: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    danger: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  },
};

// Utility function to combine classes
export const cn = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter(Boolean).join(' ');
};
